import { TransText } from '@/i18n/trans/text';
import { cn } from '@/utils/cn';
import {
  Button,
  Icons,
  Typography,
  type TypographyVariants,
} from '@fleet/webshop-ui';
import { useEffect, useRef, useState, type FC } from 'react';

interface ExpandableTextProps {
  text: string | undefined;
  variant?: TypographyVariants['variant'];
  maxLines?: 1 | 2 | 3 | 4 | 5 | 6 | 'none';
  className?: string;
  isExpanded?: boolean;
  setIsExpanded?: (value: boolean) => void;
}

const ExpandableText: FC<ExpandableTextProps> = ({
  text,
  variant = 'body2',
  maxLines = 2,
  isExpanded: isControlledExpanded,
  className,
  setIsExpanded: setControlledIsExpanded,
}) => {
  const [isUncontrolledExpanded, setIsUncontrolledExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const textRef = useRef<HTMLDivElement | null>(null);

  const isExpanded = isControlledExpanded ?? isUncontrolledExpanded;
  const setIsExpanded = setControlledIsExpanded ?? setIsUncontrolledExpanded;

  useEffect(() => {
    const checkClamping = () => {
      if (!textRef.current) return;
      const { scrollHeight, clientHeight } = textRef.current;
      setIsClamped(scrollHeight - 1 > clientHeight); // - 1 is a fix for Firefox
    };

    checkClamping();

    window.addEventListener('resize', checkClamping);
    return () => {
      window.removeEventListener('resize', checkClamping);
    };
  }, [text, isExpanded]);

  if (!text) {
    return null;
  }

  return (
    <div className={cn('mb-3', className)}>
      <div
        ref={textRef}
        style={{
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          WebkitLineClamp: isExpanded ? 'none' : maxLines,
        }}
        className="transition-all duration-300"
      >
        <Typography variant={variant}>{text}</Typography>
      </div>
      {isClamped && (
        <Button
          variant="ghost"
          data-testid="read-more-button"
          className="h-auto"
          onPress={() => setIsExpanded(!isExpanded)}
          size="small"
        >
          <TransText i18nKey="readMore" />
          <Icons.chevronDown height={12} width={12} />
        </Button>
      )}
      {isExpanded && (
        <Button
          variant="ghost"
          data-testid="read-less-button"
          className="h-auto"
          onPress={() => setIsExpanded(!isExpanded)}
          size="small"
        >
          <TransText i18nKey="showLess" />
          <Icons.chevronUp height={12} width={12} />
        </Button>
      )}
    </div>
  );
};

export default ExpandableText;
